import { useCallback, useEffect, useRef } from "react"
import { Key, KeyControl, ModifierKey } from "./types"

export const parseKeyControl = (key: KeyControl) => {
  // we need to be able to handle combinations like "shift+enter"
  const keys = key.split("+")
  const modifiers = keys.slice(0, keys.length - 1) as ModifierKey[]
  const keyName = keys[keys.length - 1] as Key
  return { modifiers, keyName }
}

const modifierKeys: ModifierKey[] = ["Meta", "Control", "Alt", "Shift"]

export type UseKeyControlOptions = {
  /**
   * When true, the listener is registered in the capture phase on `window` so it runs
   * before `keydown` reaches contenteditable (e.g. Quill) handlers that may call
   * `preventDefault` — which would otherwise make `!e.defaultPrevented` fail for later
   * bubble listeners.
   */
  capture?: boolean
  /**
   * When true, the handler is skipped for events that started inside text-entry UI.
   * Use this for page-level shortcuts that should not intercept editor/input keys.
   */
  ignoreEditableTarget?: boolean
  /**
   * The event to listen for.
   */
  event?: "keydown" | "keyup"
}

const editableTargetSelector = [
  "input",
  "textarea",
  "select",
  "[contenteditable='']",
  "[contenteditable='true']",
  "[contenteditable='plaintext-only']",
  "[role='searchbox']",
  "[role='textbox']"
].join(",")

export const isKeyboardEventFromEditableTarget = (event: KeyboardEvent) => {
  const target = event.target

  if (!(target instanceof Node)) return false

  const composedPath =
    typeof event.composedPath === "function" ? event.composedPath() : []
  const path = composedPath.length ? composedPath : [target]

  return path.some((pathTarget) => {
    if (!(pathTarget instanceof HTMLElement)) return false

    if (pathTarget.isContentEditable) return true

    return pathTarget.matches(editableTargetSelector)
  })
}

// return false to not call preventDefault on the event by default when using useKeyControl
export type KeyHandler = (e: KeyboardEvent) => void | boolean

export const handleKeyControl = (
  key: KeyControl | KeyControl[],
  handler: KeyHandler
) => {
  function innerHandler(e: KeyboardEvent) {
    // Ensure the event is a KeyboardEvent (and not some other event, like Event).
    // This is entirely possible and can occur, for instance, during form autocompletion.
    if (e instanceof KeyboardEvent) {
      const keys = Array.isArray(key) ? key : [key]

      keys.forEach((key) => {
        const { modifiers, keyName } = parseKeyControl(key)

        if (keyName.toLowerCase() !== e.key?.toLowerCase()) return

        const modifiersMatch = modifierKeys.every(
          (modifier) =>
            // All included modifiers must be active.
            (modifiers.includes(modifier) && e.getModifierState(modifier)) ||
            // All excluded modifiers must be inactive.
            (!modifiers.includes(modifier) && !e.getModifierState(modifier))
        )

        if (!modifiersMatch) return

        return handler(e)
      })
    }
  }

  // Capture the event and pass it to the inner handler.
  return (e: KeyboardEvent | React.KeyboardEvent<HTMLElement>) => {
    const event = "nativeEvent" in e ? e.nativeEvent : e
    return innerHandler(event)
  }
}

const useKeyControl = (
  key: KeyControl | KeyControl[],
  handler: KeyHandler,
  options?: UseKeyControlOptions
) => {
  // Use the ref to constrain the handler to within a specific element.
  const ref = useRef<HTMLElement>(null)

  const { capture = false, ignoreEditableTarget = false } = options ?? {}

  const keyHandler = useCallback(
    (e: KeyboardEvent) => {
      if (ignoreEditableTarget && isKeyboardEventFromEditableTarget(e)) {
        return
      }

      // Only proceeding if the event has not been prevented already by previous iteration (or some other handler).
      if (e.defaultPrevented) return

      // When using a ref, only proceed if the event target is within the ref.
      if (ref.current && !ref.current.contains(e.target as Node)) return

      const preventDefault = handleKeyControl(key, handler)(e)

      // By default, prevent same handler from being called multiple times (in the off-chance).
      if (typeof preventDefault === "boolean" && preventDefault !== false) {
        e.preventDefault()
      }
    },
    [key, handler, capture, ignoreEditableTarget]
  )

  const keyEvent = options?.event === "keyup" ? "keyup" : "keydown"

  useEffect(() => {
    window.addEventListener(keyEvent, keyHandler, capture)
    return () => window.removeEventListener(keyEvent, keyHandler, capture)
  }, [key, handler, capture, ignoreEditableTarget])

  return ref
}

export default useKeyControl
